import {
  Box,
  Button,
  ButtonProps,
  Input,
  InputGroup,
  InputLeftElement,
  useButtonGroup,
} from '@chakra-ui/react'
import { SearchTypes } from '@rolebase/shared/model/search'
import { UseComboboxStateChange, useCombobox } from 'downshift'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { SearchPerimeterProps } from '../circlePerimeter'
import useKeepOpenOnBarFocus from '../hooks/useKeepOpenOnBarFocus'
import { useSearch } from '../hooks/useSearch'
import { SearchItem } from '../searchTypes'
import SearchResultsList from './SearchResultsList'

export interface SearchButtonProps extends Omit<ButtonProps, 'onSelect'> {
  children: string
  items: SearchItem[]
  additionalTopItems?: SearchItem[]
  onSelect(id: string): void
  onCreate?(name: string): Promise<string | void>
  // ORG-02: optional perimeter integration, populated by the circle picker
  // (CircleSearchButton) only. Absent for every other search button.
  perimeter?: SearchPerimeterProps
  // Same footer action row as SearchInput (ORG-06). Added here for symmetry on
  // 10/08/2026: the member picker of a next step is a button, and its rings
  // widen through this row exactly like the input variant's.
  bottomAction?: {
    label: string
    onClick(): void
  }
  // Mounted already open, input focused: the caller swapped a display (e.g. a
  // member's vignette) for this picker on a click, and a second click to open
  // it would be one too many (#108).
  defaultIsOpen?: boolean
  // Called when the list closes, whether or not something was picked.
  onClose?(): void
}

export default function SearchButton({
  children,
  items,
  additionalTopItems,
  onSelect,
  onCreate,
  perimeter,
  bottomAction,
  defaultIsOpen,
  onClose,
  ...buttonProps
}: SearchButtonProps) {
  const { filteredItems, onInputValueChange } = useSearch(
    items,
    false,
    additionalTopItems,
    !!onCreate,
    perimeter
  )

  const inputRef = useRef<HTMLInputElement | null>(null)
  const { handleInputBlur, keepOpenReducer, wrapHeader } =
    useKeepOpenOnBarFocus(inputRef)

  const onSelectedItemChange = useCallback(
    async (changes: UseComboboxStateChange<SearchItem>) => {
      const item = changes.selectedItem
      if (!item) return
      closeMenu()

      if (onCreate && item.type === SearchTypes.CreateAction) {
        // Create entity then select it
        const id = await onCreate(item.text)
        if (id) onSelect(id)
      } else {
        // Select existing entity
        onSelect(item.id)
      }

      selectItem(undefined as any)
      setInputValue('')
      buttonRef.current?.focus()
    },
    [onSelect, onCreate]
  )

  const {
    isOpen,
    openMenu,
    closeMenu,
    highlightedIndex,
    setInputValue,
    selectItem,
    getMenuProps,
    getInputProps,
    getItemProps,
  } = useCombobox({
    items: filteredItems,
    itemToString: () => '',
    defaultHighlightedIndex: 0,
    initialIsOpen: defaultIsOpen,
    onIsOpenChange: ({ isOpen }) => {
      if (!isOpen) onClose?.()
    },
    // ORG-02: skip the non-selectable perimeter separator row in keyboard nav
    isItemDisabled: (item) => !!item?.isSeparator || !!item?.disabledReason,
    onInputValueChange,
    onSelectedItemChange,
    stateReducer: (state, actionAndChanges) =>
      keepOpenReducer(state, actionAndChanges),
  })

  // Button
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [buttonWidth, setButtonWidth] = useState<number | undefined>(undefined)
  const buttonGroup = useButtonGroup()
  const size = buttonProps.size || buttonGroup?.size

  useLayoutEffect(() => {
    setButtonWidth(buttonRef.current?.offsetWidth)
  }, [children])

  // Input
  const inputProps = getInputProps({
    ref: inputRef,
    onFocus: openMenu,
    onBlur: handleInputBlur,
  })

  useEffect(() => {
    if (defaultIsOpen) inputRef.current?.focus()
  }, [])

  // Click on button to put it in editing mode
  const handleClick = () => {
    openMenu()
    // Wait for the input to appears, then focus it
    setTimeout(() => {
      inputRef.current?.focus()
    }, 0)
  }

  return (
    <Box>
      <InputGroup size={size}>
        {!isOpen && (
          <Button ref={buttonRef} {...buttonProps} onClick={handleClick}>
            {children}
          </Button>
        )}
        {isOpen && buttonProps.leftIcon && (
          <InputLeftElement pointerEvents="none" pl={3}>
            {buttonProps.leftIcon}
          </InputLeftElement>
        )}
        <Input
          type="text"
          placeholder={children}
          display={isOpen ? '' : 'none'}
          w={buttonWidth ? `${buttonWidth}px` : 'auto'}
          pl={buttonProps.leftIcon ? 10 : undefined}
          borderRadius={buttonProps.borderRadius}
          {...inputProps}
        />
      </InputGroup>

      <SearchResultsList
        items={filteredItems}
        isOpen={isOpen}
        highlightedIndex={highlightedIndex}
        getMenuProps={getMenuProps}
        getItemProps={getItemProps}
        inputRef={inputRef}
        showCanCreate={!!onCreate}
        flat={!!perimeter?.isPartitioned}
        bottomAction={bottomAction}
        header={perimeter && wrapHeader(perimeter.header, closeMenu)}
      />
    </Box>
  )
}
